import { getFormProps, useForm } from "@conform-to/react";
import { parseWithZod } from "@conform-to/zod";
import { Block, Button, f7, Icon, List, ListInput } from "framework7-react";
import { useState } from "react";

import { axios } from "@/config/axios";
import { resetPasswordBody, useResetPassword } from "@/generated/api";

type PasswordResetFormProps = {
  authHeaders?: {
    accessToken: string;
    client: string;
    uid: string;
  };
  onCompleted: VoidFunction;
  resetToken: string;
};

export function PasswordResetForm({ authHeaders, onCompleted, resetToken }: PasswordResetFormProps) {
  const { mutate, isPending: isMutationPending } = useResetPassword();
  const [isManualPending, setIsManualPending] = useState(false);
  const isPending = isMutationPending || isManualPending;
  const formSchema = resetPasswordBody;

  const [form, fields] = useForm({
    defaultValue: {
      password: "",
      password_confirmation: "",
      reset_password_token: resetToken,
    },
    onSubmit(event, { formData }) {
      event.preventDefault();

      const submission = parseWithZod(formData, { schema: formSchema });

      if (submission.status !== "success") {
        return submission.reply();
      }

      if (authHeaders) {
        const payload = { ...submission.value };
        setIsManualPending(true);
        axios({
          data: payload,
          headers: {
            "Content-Type": "application/json",
            "access-token": authHeaders.accessToken,
            client: authHeaders.client,
            uid: authHeaders.uid,
          },
          method: "PUT",
          url: "/auth/password",
        })
          .then(() => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "パスワードを更新しました",
            });
            onCompleted();
          })
          .catch(() => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "パスワードの再設定に失敗しました",
            });
          })
          .finally(() => {
            setIsManualPending(false);
          });
        return;
      }

      mutate(
        { data: submission.value },
        {
          onError: () => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "パスワードの再設定に失敗しました",
            });
          },
          onSuccess: () => {
            f7.toast.create({
              closeTimeout: 2000,
              text: "パスワードを更新しました",
            });
            onCompleted();
          },
        },
      );
    },
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: formSchema });
    },
    shouldValidate: "onBlur",
  });

  return (
    <form {...getFormProps(form)}>
      <input
        defaultValue={resetToken}
        name={fields.reset_password_token.name}
        type="hidden"
      />
      <List>
        <ListInput
          clearButton
          floatingLabel
          required
          validate
          autocomplete="new-password"
          info="新しいパスワードを入力してください"
          label="新しいパスワード"
          name={fields.password.name}
          placeholder="password@123456"
          type="password"
        >
          <Icon
            material="key"
            slot="media"
          />
        </ListInput>

        <ListInput
          clearButton
          floatingLabel
          required
          validate
          info="新しいパスワードを入力してください"
          label="新しいパスワード (確認用)"
          name={fields.password_confirmation.name}
          placeholder="password@123456"
          type="password"
        >
          <Icon
            material="key"
            slot="media"
          />
        </ListInput>
      </List>
      <Block>
        <Button
          fill
          large
          loading={isPending}
          type="submit"
        >
          パスワード更新
        </Button>
      </Block>
    </form>
  );
}
